/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { ChartProps } from '../../Chart.props';
import { COLUMN_CHART_HEADER_POSITIONS, ColumnChartPlottingData } from './ColumnChart.defs';

/**
 * The props for SDS Column chart component.
 */
export interface ColumnChartProps extends ChartProps {
  /**
   * The classes to apply to the Column chart.
   */
  classes?: string;

  /**
   * The categories for the Column chart. Represents the x-axis values, on which one or more columns should be shown.
   */
  categories: Array<string>;

  /**
   * The label for the x-axis.
   */
  xAxisLabel?: string;

  /**
   * The label for the y-axis.
   */
  yAxisLabel?: string;

  /**
   * The plotting data for the Column chart. Represents the y-axis values, which should be plotted against the x-axis values, meaning - if using an array of categories: [USA, BRAZIL], you need to input an array consisting of at least one object (representing one column in each category), which has exactly two values in the data property, to represent the values for USA and for BRAZIL categories.
   */
  plottingData: Array<ColumnChartPlottingData>;

  /**
   * The position of the title and subtitle in the chart.
   * @default 'center'
   */
  headerAlignment?: keyof typeof COLUMN_CHART_HEADER_POSITIONS;

  /**
   * The minimum value for the X-axis.
   */
  minX?: number;

  /**
   * The maximum value for the X-axis.
   */
  maxX?: number;

  /**
   * Adds tick marks on both axis of the chart.
   * @default false
   */

  tickMarks?: boolean;

  /**
   * Defines if chart should have a grid.
   * @default false
   */
  showGrid?: boolean;

  /**
   * Defines if chart lines (axisY, axisX) are bolder and of black color (axisY, axisX, grid-lines).
   * @default false
   */
  boldLines?: boolean;

  /**
   * Controls whether chart should be static, or interactive (meaning should have hover effects, tooltips, etc.).
   * @default false
   */
  staticChart?: boolean;

  /**
   * Controls whether the legend should be hidden.
   * @default false
   */
  hideLegend?: boolean;
}

/**
 * The default options of a column chart.
 */
export const columnChartDefaults: ColumnChartProps = {
  categories: [],
  classes: 'sds-column-chart',
  plottingData: [],
};
